import type { ChangeEvent } from 'react'
import { useCallback, useState } from 'react'

import { useUnit } from 'effector-react'

import { Page } from '@/components/layout/page/page.tsx'
import { useDateRangeContext } from '@/components/provider/date-range-provider.tsx'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker.tsx'
import { WithRefresher } from '@/components/ui/with-refresher.tsx'
import { $creatorId } from '@/models/creators.ts'
import { useSpendingList } from '@/modules/tracking/new-subscriber-analysis/hooks/use-spending-list.ts'
import { useSubscribersAnalysis } from '@/modules/tracking/new-subscriber-analysis/hooks/use-subscribers-analysis.ts'
import { SubscriptionLengthChart } from '@/modules/tracking/new-subscriber-analysis/ui/charts/subscription-lenght-chart.tsx'
import { AverageResponseTime } from '@/modules/tracking/new-subscriber-analysis/ui/components/average-response-time.tsx'
import { InteractionOverview } from '@/modules/tracking/new-subscriber-analysis/ui/components/interaction-overview.tsx'
import { PurchasingBehaviour } from '@/modules/tracking/new-subscriber-analysis/ui/components/purchasing-behaviour.tsx'
import { SpendingCustomerConversion } from '@/modules/tracking/new-subscriber-analysis/ui/components/spending-customer-conversion.tsx'
import { HighSpendingCustomerList } from '@/modules/tracking/new-subscriber-analysis/ui/dialog/high-spending-customer-list.tsx'
import { LowSpendingCustomerList } from '@/modules/tracking/new-subscriber-analysis/ui/dialog/low-spending-customer-list.tsx'

const DefaultHighSpendLimit = 100
const DefaultLowSpendLimit = 10

export const NewSubscriberAnalysisPage = () => {
  const { date, setDate } = useDateRangeContext()
  const [
    isHighSpendCustomerListDialogOpen,
    setHighSpendCustomerListDialogOpen,
  ] = useState(false)
  const [isLowSpendCustomerListDialogOpen, setLowSpendCustomerListDialogOpen] =
    useState(false)

  const [highSpendLimit, setHighSpendLimit] = useState<number | undefined>(
    DefaultHighSpendLimit,
  )
  const [lowSpendLimit, setLowSpendLimit] = useState<number | undefined>(
    DefaultLowSpendLimit,
  )
  const creatorId = useUnit($creatorId)

  const handleHighLimitChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value
      const size = inputValue === '' ? undefined : Number(inputValue)

      setHighSpendLimit(size)
    },
    [setHighSpendLimit],
  )

  const handleLowLimitChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value
      const size = inputValue === '' ? undefined : Number(inputValue)

      setLowSpendLimit(size)
    },
    [setLowSpendLimit],
  )

  const { subscribersAnalysis, isFetching, refetch } = useSubscribersAnalysis(
    date,
    creatorId || 0,
  )

  const {
    spendingList: highSpendLimitData,
    isFetching: isHighSpendLimitFetching,
  } = useSpendingList(
    date,
    creatorId!,
    { pageSize: 0, pageIndex: 0 },
    true,
    highSpendLimit!,
    true,
  )

  const {
    spendingList: lowSpendLimitData,
    isFetching: isLowSpendLimitFetching,
  } = useSpendingList(
    date,
    creatorId!,
    { pageSize: 0, pageIndex: 0 },
    false,
    lowSpendLimit!,
    true,
  )

  const totalSubscribersCount = subscribersAnalysis?.subscriptionLengthSum || 0
  const numberOfFanWhoBoughtPPV =
    subscribersAnalysis?.numberOfFanWhoBoughtPPV || 0
  const highSpendingFansCount = highSpendLimitData?.totalElements || 0
  const lowSpendingFansCount = lowSpendLimitData?.totalElements || 0

  const closeHighSpendCustomerList = useCallback(() => {
    setHighSpendCustomerListDialogOpen(false)
  }, [setHighSpendCustomerListDialogOpen])

  const closeLowSpendCustomerList = useCallback(() => {
    setLowSpendCustomerListDialogOpen(false)
  }, [setLowSpendCustomerListDialogOpen])

  const refetchSubscribersAnalysis = useCallback(() => {
    refetch()
  }, [refetch])

  return (
    <Page
      className="mb-8"
      title="New-Subscriber Analysis"
      headerOptions={
        <WithRefresher
          isFetching={isFetching}
          onRefresh={refetchSubscribersAnalysis}
        >
          <CalendarRangeDatePicker
            date={date}
            setDate={setDate}
          />
        </WithRefresher>
      }
    >
      <div className="grid gap-8">
        <div className="grid grid-flow-row gap-6 sm:gap-12 md:grid-cols-1 xl:gap-8 2xl:grid-cols-2">
          <div className="grid gap-6">
            <div className="text-xl font-semibold leading-none sm:text-2xl">
              Welcome Message Interactions
            </div>
            <div className="grid grid-flow-row grid-cols-1  gap-4 sm:h-72  sm:grid-cols-2  xl:gap-8">
              <AverageResponseTime
                isFetching={isFetching}
                repliedSubscribers={
                  subscribersAnalysis?.interactionOverviewData.replied || 0
                }
                time={
                  subscribersAnalysis?.averageWelcomeMessageResponseTime || 0
                }
              />
              <InteractionOverview
                isFetching={isFetching}
                subscribersAnalysis={subscribersAnalysis}
              />
            </div>
          </div>
          <div className="grid gap-6">
            <div className="text-xl font-semibold leading-none sm:text-2xl">
              Purchasing Behaviour
            </div>
            <div className="grid gap-4 sm:h-72">
              <PurchasingBehaviour
                total={totalSubscribersCount}
                isFetching={isFetching}
                numberOfFanWhoBoughtPPV={numberOfFanWhoBoughtPPV}
              />
            </div>
          </div>
        </div>
        <div className="grid gap-6">
          <div>
            <div className="text-xl font-semibold  sm:text-2xl">
              Subscription Length
            </div>
            <div className="mt-1 text-sm  leading-5">
              This chart shows how many new subscribers stayed during your set
              timeframe and how many new subscribers unsubscribed within your
              set timeframe.
            </div>
          </div>

          {isFetching ? (
            <div className="grid h-80 min-h-80 w-full animate-pulse gap-4 rounded-md bg-skeleton sm:h-72" />
          ) : (
            <SubscriptionLengthChart
              statistics={{
                subscribed:
                  subscribersAnalysis?.subscriptionLengthData.active || 0,
                unsubscribed:
                  subscribersAnalysis?.subscriptionLengthData.expired || 0,
              }}
            />
          )}
        </div>
        <div className="grid gap-6">
          <div className="l:gap-8 grid grid-flow-row gap-6 sm:gap-12 md:grid-cols-1 2xl:grid-cols-2">
            <SpendingCustomerConversion
              isFetching={isHighSpendLimitFetching}
              title="High-Spending Customer Conversion"
              description={
                'This number shows how many new subscribers converted to \n' +
                'High-Spending Customers during your set timeframe and minimum spend.'
              }
              donateHighType
              total={totalSubscribersCount}
              limit={highSpendLimit!}
              subscribersAnalysis={subscribersAnalysis}
              setSpendCustomerListDialogOpen={
                setHighSpendCustomerListDialogOpen
              }
              setLimit={handleHighLimitChange}
              spendingFansCount={highSpendingFansCount}
            />

            <SpendingCustomerConversion
              isFetching={isLowSpendLimitFetching}
              title="Low-Spending Customer Conversion"
              description="This number shows how many new subscribers converted to Low-Spending Customers during your set timeframe and maximum spend."
              total={totalSubscribersCount}
              limit={lowSpendLimit!}
              setLimit={handleLowLimitChange}
              subscribersAnalysis={subscribersAnalysis}
              setSpendCustomerListDialogOpen={setLowSpendCustomerListDialogOpen}
              spendingFansCount={lowSpendingFansCount}
            />
          </div>
        </div>
      </div>

      <HighSpendingCustomerList
        isOpen={isHighSpendCustomerListDialogOpen}
        onClose={closeHighSpendCustomerList}
        creatorId={creatorId!}
        limit={highSpendLimit!}
        date={date}
      />

      <LowSpendingCustomerList
        isOpen={isLowSpendCustomerListDialogOpen}
        onClose={closeLowSpendCustomerList}
        creatorId={creatorId!}
        limit={lowSpendLimit!}
        date={date}
      />
    </Page>
  )
}
